// Tiny inline keyboard diagram for the inversions lesson: a handful of keys
// around one voicing with the chord tones filled in — indigo for the upper
// notes, emerald for the bass, since the bass note is the whole point of an
// inversion. Purely decorative (aria-hidden); the playable keys live on the
// page's full PianoRoll.
//
// Props: {
//   midis: number[] — the chord's MIDI notes to highlight,
//   accent?: number | null — a midi note to mark amber instead of indigo,
//     used to call out the added 7th in seventh-chord diagrams; the bass
//     note stays emerald regardless of accent,
// }

import React from "react";
import { isBlackKey } from "../../lib/pianoDrills";

const WHITE_W = 13;
const WHITE_H = 42;
const BLACK_W = 8;
const BLACK_H = 26;

export default function MiniChordDiagram({ midis, accent = null }) {
  if (!midis || !midis.length) return null;
  let lo = Math.min(...midis) - 1;
  let hi = Math.max(...midis) + 1;
  while (isBlackKey(lo)) lo -= 1;
  while (isBlackKey(hi)) hi += 1;

  const keys = [];
  let wi = 0;
  for (let m = lo; m <= hi; m += 1) {
    const black = isBlackKey(m);
    keys.push({ m, black, wi: black ? wi - 1 : wi });
    if (!black) wi += 1;
  }
  const width = wi * WHITE_W;
  const bass = Math.min(...midis);
  const fillFor = (m, black) => {
    if (m === bass) return "#34d399"; // emerald-400 — the bass
    if (m === accent) return "#fbbf24"; // amber-400 — the added 7th
    if (midis.includes(m)) return "#818cf8"; // indigo-400 — the rest
    return black ? "#0f172a" : "#ffffff";
  };

  return (
    <svg
      width={width}
      height={WHITE_H}
      viewBox={`0 0 ${width} ${WHITE_H}`}
      className="rounded border border-slate-200"
      aria-hidden
    >
      {keys
        .filter((k) => !k.black)
        .map((k) => (
          <rect
            key={k.m}
            x={k.wi * WHITE_W}
            y={0}
            width={WHITE_W}
            height={WHITE_H}
            fill={fillFor(k.m, false)}
            stroke="#cbd5e1"
            strokeWidth="0.75"
          />
        ))}
      {keys
        .filter((k) => k.black)
        .map((k) => (
          <rect
            key={k.m}
            x={(k.wi + 1) * WHITE_W - BLACK_W / 2}
            y={0}
            width={BLACK_W}
            height={BLACK_H}
            fill={fillFor(k.m, true)}
            stroke="#0f172a"
            strokeWidth="0.5"
          />
        ))}
    </svg>
  );
}
